import { ReactNode, useEffect, useState } from 'react';
import logger from '@logger';
import useTranslation from 'next-translate/useTranslation';
import { useAppDispatch, useAppSelector } from '@hooks/useAppDispatch';
import useCustomer from '@hooks/useCustomer';
import trackCompletedSection from '@helpers/analyticsHelpers/trackCompletedSection';
import trackPlaceOrderClick from '@helpers/analyticsHelpers/trackPlaceOrderClick';
import { ErrorDataType, PossiblyWrongStoreConfirmedState } from '@molecules/CheckoutPaymentButton/CheckoutPaymentButton';
import { isAxiosError } from 'axios';
import {
  selectCheckoutPayment,
  setCompleted,
  setEditing,
  setErrorMessage,
  setSelectedMode,
} from '@slices/checkoutSlice';
import {
  StyledCheckoutSubmitButton,
  StyledSwedbankPayLogo,
  StyledText,
} from '@molecules/CheckoutPaymentButton/CheckoutPaymentButton.styles';
import { placeOrder } from '@api/interfaces/checkoutApi';
import useUserAgent from '@hooks/useUserAgent';
import webToAppApi from '@api/web-to-app';
import { CreditCardPaymentInfoData } from '@occ/api-client';
import useCheckCartConflict from '@hooks/useCheckCartConflict';
import { PlaceOrderErrorResponse } from '@enums/PlaceOrderErrorResponse';
import paths from '@constants/paths';
import useCustomRouter from '@hooks/useCustomRouter';
import { setEmptyCartModalVisibility } from '@slices/modalSlice';
import useStore from '@hooks/useStore';
import useCart from '@hooks/useCart';

interface Props {
  disabled?: boolean;
  children: ReactNode;
  saveCard?: boolean;
  selectedCard?: CreditCardPaymentInfoData;
  setError: (error: ErrorDataType) => void;
  possiblyWrongStoreConfirmed: PossiblyWrongStoreConfirmedState;
}

const PayExButton = ({
  saveCard = false,
  selectedCard = {
    cardType: '',
    defaultCard: false,
    expireDate: '',
    expired: false,
    id: '',
    maskedNumber: '',
  },
  children,
  disabled = false,
  setError,
  possiblyWrongStoreConfirmed,
}: Props) => {
  const { t } = useTranslation('checkoutpage');
  const payment = useAppSelector(selectCheckoutPayment);
  const { customer } = useCustomer();
  const { cart } = useCart();
  const checkCartConflict = useCheckCartConflict({ checkStock: true });
  const [disablePayment, setDisablePayment] = useState(disabled);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isNativeApp } = useUserAgent();
  const dispatch = useAppDispatch();
  const router = useCustomRouter();
  const { store } = useStore();

  useEffect(() => {
    const activeCards = customer?.savedCards?.filter((card) => !card.expired);
    if (customer?.savedCards?.length && activeCards && activeCards.length > 0) {
      if (payment.selectedCard.id) {
        setDisablePayment(false);
      } else {
        setDisablePayment(true);
      }
    } else {
      setDisablePayment(disabled);
    }
  }, [payment.selectedCard?.id, customer?.savedCards, disabled]);

  const submitPayment = async () => {
    trackPlaceOrderClick();
    setDisablePayment(true);
    setIsSubmitting(true);
    try {
      await checkCartConflict({ storeId: store?.storeId });
      const response = await placeOrder({
        orderRef: cart?.orderReference,
        saveCard: customer?.isAnonymous ? false : saveCard,
        selectedCardId: selectedCard?.id === 'new' || selectedCard?.expired ? '' : (selectedCard?.id as string),
        klarnaAuthorizationToken: '',
        userClickedContinue:
          possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.CONFIRMED_IN_DELIVERY ||
          possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.CONFIRMED_IN_MODAL,
      });
      trackCompletedSection('payment');

      if (isNativeApp) {
        webToAppApi.paymentstarted();
      }

      const { location } = response.headers;
      window.location.href = location;
    } catch (e) {
      if (isAxiosError(e)) {
        if (e.request.response === PlaceOrderErrorResponse.NO_DELIVERY_SLOT) {
          dispatch(setCompleted({ boxName: 'delivery', isCompleted: false }));
          dispatch(setEditing({ boxName: 'delivery', isEditing: true }));
          dispatch(setErrorMessage(e.request.response));
          setError({ errorCode: e.request.response });
        } else if (e.request.response === PlaceOrderErrorResponse.EMPTY_CART) {
          dispatch(setEmptyCartModalVisibility(true));
          router.push(paths.HOME);
        } else if (e.request.response === PlaceOrderErrorResponse.WRONG_STORE) {
          setError({ errorCode: e.request.response });
        } else {
          setError({ errorCode: e.request.response || 'default' });
          if (isNativeApp) {
            webToAppApi.paymentended();
          }
          dispatch(setSelectedMode(''));
          logger.error({ error: 'PayEx error', message: e.message });
        }
      }
      setDisablePayment(false);
      setIsSubmitting(false);
    } finally {
      if (selectedCard.id === 'new' && isNativeApp) {
        webToAppApi.eventCreditCardUpdate();
      }
    }
  };

  useEffect(() => {
    if (possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.CONFIRMED_IN_MODAL) {
      submitPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [possiblyWrongStoreConfirmed]);

  return (
    <>
      <StyledCheckoutSubmitButton
        theme="primary"
        onClick={async (e) => {
          if (!(e.target as HTMLButtonElement)?.closest<HTMLFormElement>('form:invalid')) {
            await submitPayment();
          }
        }}
        type="submit"
        data-testid="payex-checkout-button"
        disabled={disablePayment}
        isSubmitting={isSubmitting}
      >
        {children}
      </StyledCheckoutSubmitButton>
      <StyledText type="body">{t('text->handledBySwedbankPay')}</StyledText>
      <StyledSwedbankPayLogo src="/icons/logos/SwedbankPay.svg" alt="Swedbank Pay Logo" />
    </>
  );
};

export default PayExButton;
